/**
 * B60: SOMEONE WHO JOINS AN ANNOUNCEMENT'S AUDIENCE AFTER IT WAS PUBLISHED.
 *
 * A studio announcement's must-acknowledge list (expectedRecipientUids) is
 * resolved once, when it is published: every active account the audience
 * roles covered at that moment, minus the author. A family linked a day
 * later reads the announcement by role (the read rule is audience-scoped),
 * but she is not on that list, so before this change the card sat on her
 * screen with no Understood button, never counted as waiting on her, and the
 * rules refused her acknowledgement. Kim's Waiting names never showed her.
 *
 * THE FIX, smallest that is correct: a still-current must-acknowledge
 * announcement whose audience covers her role adds HER, and only her, to the
 * list through the server lane (staffParentBulletins.mjs, the bulletins
 * viewer lane), which re-checks everything below with the Admin SDK. Once
 * she is on the list every existing surface treats her exactly like someone
 * who was there at publish: the Understood button, the home count, the
 * Messages must-read, the rules, and Kim's Waiting names. Zero rules bytes.
 *
 * WHAT IT NEVER DOES: alert her. No push, no email, no bell card. Whether a
 * late joiner should get an alert is the studio owner's question, not a
 * default (B60 report). A later Show again does reach her, because by then
 * she is a recipient.
 *
 * ONE DEFINITION, shared by the browser (which decides whether to ask) and
 * the lane (which decides whether to write), so the two cannot drift.
 * Pure on purpose: no React, no Firebase, so node --test reaches it directly
 * and the .mjs lane can import it.
 */

/** Mirrors BULLETIN_HOVER_WEEKS in bulletins.ts: still current means not yet archived. */
export const LATE_RECIPIENT_HOVER_MS = 6 * 7 * 24 * 60 * 60 * 1000;
/** The same ceiling the server publish lane enforces (messageAttachmentWrite.mjs). */
export const LATE_RECIPIENT_LIST_MAX = 500;

export interface LateRecipientBulletin {
  ackRequired?: unknown;
  audienceRoles?: unknown;
  expectedRecipientUids?: unknown;
  createdBy?: unknown;
  publishAt?: unknown;
  expiresAt?: unknown;
  createdAt?: unknown;
  resentAt?: unknown;
  /** Browser-only B14 flag: a staff parent reading a family announcement. */
  viaDancerLink?: unknown;
}

export interface LateRecipientViewer {
  uid: string;
  role?: string | null;
  status?: string | null;
}

/** Millis from a Firestore Timestamp (either SDK) or null. */
function millisOf(value: unknown): number | null {
  if (value && typeof value === 'object'
      && typeof (value as { toMillis?: unknown }).toMillis === 'function') {
    const ms = (value as { toMillis: () => number }).toMillis();
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
}

/**
 * Whether this viewer is someone the audience covers NOW who was not on the
 * list when it was published, on an announcement still asking for an answer.
 * Every half is a reason the answer must be no:
 *   - not must-acknowledge: there is no list to join; a plain announcement
 *     already reaches her by role (read and Got it).
 *   - viaDancerLink: a staff parent reads a family announcement but is not
 *     a must-acknowledge recipient (Bobbie, DECISIONS 155: "read and Got it
 *     only").
 *   - not an active account, or the audience does not name her role.
 *   - she wrote it, or she is already on the list.
 *   - not live yet, expired, or past the six-week window (archived).
 *   - the list is full.
 */
// The same role set the publish lane resolves recipients from
// (messageAttachmentWrite.mjs USER_ROLES), so an empty audience never
// lets in a role the publish step would have skipped.
const PUBLISH_ROLES = new Set(['director', 'manager', 'company', 'ensemble', 'foh', 'family']);

export function isLateBulletinRecipient(
  bulletin: LateRecipientBulletin,
  viewer: LateRecipientViewer,
  nowMs: number
): boolean {
  if (bulletin.ackRequired !== true) return false;
  if (bulletin.viaDancerLink === true) return false;
  if (!viewer.uid || viewer.status !== 'active' || !viewer.role) return false;
  if (!PUBLISH_ROLES.has(viewer.role)) return false;
  const roles = Array.isArray(bulletin.audienceRoles) ? bulletin.audienceRoles : null;
  if (!roles) return false;
  if (roles.length > 0 && !roles.includes(viewer.role)) return false;
  if (bulletin.createdBy === viewer.uid) return false;
  const listed = Array.isArray(bulletin.expectedRecipientUids) ? bulletin.expectedRecipientUids : [];
  if (listed.includes(viewer.uid)) return false;
  if (listed.length >= LATE_RECIPIENT_LIST_MAX) return false;
  // Live: a missing publishAt is an older announcement, live since written.
  // A pending or malformed one is not a date we can promise.
  if (bulletin.publishAt !== undefined && bulletin.publishAt !== null) {
    const publishMs = millisOf(bulletin.publishAt);
    if (publishMs === null || publishMs > nowMs) return false;
  }
  if (bulletin.expiresAt !== undefined && bulletin.expiresAt !== null) {
    const expiresMs = millisOf(bulletin.expiresAt);
    if (expiresMs === null || expiresMs <= nowMs) return false;
  }
  // Still current: created or last shown again within the hover window,
  // the same anchor bulletinRecencyMs uses. No resolved date is not current.
  const createdMs = millisOf(bulletin.createdAt) ?? 0;
  const resentMs = millisOf(bulletin.resentAt) ?? 0;
  const recencyMs = Math.max(createdMs, resentMs);
  if (recencyMs <= 0 || recencyMs < nowMs - LATE_RECIPIENT_HOVER_MS) return false;
  return true;
}

/**
 * Whether a join ask that did not land may be asked again on a later
 * announcement snapshot. A network failure (no status), a rate limit (429)
 * or a server fault (5xx) is passing trouble, so yes. Any other answer is
 * the lane saying no (or yes), so never: a refused caller must not loop.
 */
export function lateJoinMayRetry(status: number | null): boolean {
  return status === null || status === 429 || status >= 500;
}
